import * as Sentry from '@sentry/browser';
import { isDiscordActivity } from '@/utils/discordActivity';

function buildSentryTunnelUrl(dsn: string): string | null {
  if (typeof window === 'undefined') return null;
  if (!isDiscordActivity()) return null;

  try {
    const dsnUrl = new URL(dsn);
    const publicKey = (dsnUrl.username || '').trim();
    const pathParts = (dsnUrl.pathname || '').split('/').filter(Boolean);
    const projectId = (pathParts.pop() || '').trim();
    const dsnPathPrefix = pathParts.length > 0 ? `/${pathParts.join('/')}` : '';

    if (!publicKey || !projectId) return null;

    const query = new URLSearchParams({
      sentry_version: '7',
      sentry_key: publicKey,
      sentry_client: `sentry.javascript.browser/${Sentry.SDK_VERSION}`,
    });

    return `${window.location.origin}/.proxy/sentry${dsnPathPrefix}/api/${projectId}/envelope/?${query.toString()}`;
  } catch (error) {
    captureError(error, { scope: 'sentry', action: 'buildSentryTunnelUrl' });
    return null;
  }
}

export function initSentry(): void {
  // Only initialize in production or if DSN is provided
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (dsn) {
    const tunnel = buildSentryTunnelUrl(dsn);
    Sentry.init({
      dsn,
      tunnel: tunnel ?? undefined,
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
      tracesSampleRate: 0.1,
    });
  } else if (import.meta.env.DEV) {
    console.log('Sentry DSN not configured, error tracking disabled');
  }
}

export function captureError(error: unknown, context?: Record<string, unknown>): void {
  const err = error instanceof Error
    ? error
    : new Error(typeof error === 'string' ? error : 'Unknown error');

  console.error('Error:', err);

  if (context || !(error instanceof Error)) {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setContext('gameContext', context);
      }
      if (!(error instanceof Error)) {
        scope.setExtra('rawError', error);
      }
      scope.captureException(err);
    });
    return;
  }

  Sentry.captureException(err);
}

export function setUser(id: string): void {
  Sentry.setUser({ id });
}
